/**
 * @GestionalePD User charge details UI
 */
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Container, Card, Grid, CardContent, CardHeader, IconButton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

//---------------------------------------------------------------------------------------
import { CustomTextField, CustomDatePicker } from '../../../../utils/StyledInputs';
import { HttpErrorHandler, ErrorUI } from '../../../../utils/ErrorHandler';
import { EmptyPageUI, LoadingUI } from '../../../../utils/LayoutStyles';
import { PositionStatusLabel } from '../../../../utils/PositionStatusLabel';
import API from '../../../../api';

dayjs.locale('it');

/**
 * @returns User charge details UI
 */
export default function UserPositionDetail() {
  // general
  const { state } = useLocation();
  const { chargeID } = state;
  const [charge, setCharge] = useState([]);
  const [hasCharge, setHasCharge] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // charge details variables declaration
  const [chargeStartDate, setChargeStartDate] = useState('');
  const [chargeEndDate, setChargeEndDate] = useState('');
  const [chargeInstitutionalEmail, setChargeInstitutionalEmail] = useState('');
  const [chargeUserName, setChargeUserName] = useState('');

  // get charge api details by charge id
  const getPositionDetail = () => {
    setErrorMessage(null);

    API.get(`/charges/${chargeID}`)
      .then((response) => {
        // charge api data assign to variables
        setCharge(response.data);
        setChargeStartDate(response.data.start_date);
        setChargeEndDate(response.data.end_date);
        setChargeInstitutionalEmail(response.data.email);
        setChargeUserName(`${response.data.user_name} ${response.data.user_surname}`);
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => setHasCharge(true));
  };

  // initial charge data loading
  useEffect(() => {
    getPositionDetail();
  }, []);

  // if charge id not found redirect to 404 page
  if (!chargeID) return <Navigate to={{ pathname: '/404' }} />;

  return hasCharge ? (
    <Container maxWidth="xl">
      <Grid container height="100%">
        {charge.length !== 0 && !errorMessage ? (
          <Grid item xs={12}>
            {/* charge details card */}
            <Card sx={{ minWidth: 275 }}>
              <CardHeader
                title={charge.position_name}
                subheader={chargeUserName}
                action={<IconButton disabled>{PositionStatusLabel(charge, 'icon')}</IconButton>}
              />

              <CardContent>
                <Grid container spacing={3}>
                  {/* charge institutional e-mail */}
                  <Grid item xs={12}>
                    <CustomTextField
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      name="charge-email"
                      label="E-mail Istituzionale"
                      type="email"
                      defaultValue={chargeInstitutionalEmail}
                    />
                  </Grid>

                  {/* charge start date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <CustomDatePicker readOnly label="Data Inizio" value={dayjs(chargeStartDate)} />
                    </LocalizationProvider>
                  </Grid>

                  {/* charge end date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <CustomDatePicker readOnly label="Data Fine" value={dayjs(chargeEndDate)} />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ) : errorMessage ? (
          // error handler UI
          ErrorUI(errorMessage)
        ) : (
          // empty page ui
          EmptyPageUI('Nessuna carica trovata')
        )}
      </Grid>
    </Container>
  ) : (
    // loading spinner UI
    LoadingUI()
  );
}
